/**
 * The client device specification the SPA reports to admin-api on every
 * request, so `app.auth_login_events` can record what a person signed in on.
 *
 * The server sees `user-agent` and the source ip and nothing else — screen
 * geometry lives only in the browser. Bug reports already carry a device
 * picture (`captureDomSnapshot` in console-capture.ts); this is the same idea
 * on the authentication path, where until now a login row could not say
 * whether it came from a phone or a 4K desktop.
 *
 * ⚠️ SCREEN, not viewport. A login row is written once per token issuance
 * (see apps/admin-api/src/auth/login-tracking.ts), so a window size captured
 * at that instant describes a moment, not a device — resizing the window
 * produces no new row and the stored number silently goes stale. Screen
 * geometry and pixel ratio are properties of the hardware, so they stay true
 * for the life of the row. Bug reports want the opposite and capture the
 * viewport, because there the question is what the page was laid out in.
 *
 * The wire format is JSON so a field can be added later without a migration —
 * the column is a single `jsonb`, and the server ignores keys it does not
 * know. Keep the payload small: it rides on EVERY request.
 */

/** Sent on every request from `apiFetch` and the observability POST. */
export const CLIENT_DEVICE_HEADER = 'x-client-device';

/**
 * ⚠️ Declared a second time, by hand, as `ClientDevice` in
 * `packages/db/src/app/_auth-login-events.ts` — the column's own type — and
 * parsed by `parseClientDevice` in `apps/admin-api/src/auth/client-device.ts`.
 * Nothing joins the two, so a renamed key here means the server stores null
 * rather than failing: quiet, and only visible in the data. Change all three.
 */
export interface ClientDevice {
  screenWidth:       number;
  screenHeight:      number;
  devicePixelRatio:  number;
}

/**
 * Read the device spec off a window, or null when it cannot be established.
 *
 * The window is passed in rather than reached for so this is callable from a
 * plain test with no DOM — the browser is a host, and a value read out of one
 * is not a reason to start one.
 */
export function collectClientDevice(win: Partial<Window> | undefined): ClientDevice | null {
  const screen = win?.screen;
  if (!screen) return null;

  const screenWidth  = Math.round(screen.width);
  const screenHeight = Math.round(screen.height);
  // A ratio is fractional on most non-integer-scaled displays, so it is
  // rounded to 3 decimals rather than to an integer — the fraction is the
  // interesting part. THREE, not two: 2.625 is an ordinary Android value and
  // two decimals rounds it to 2.63, inventing a ratio no device reports.
  const ratio = Math.round((win?.devicePixelRatio ?? 1) * 1000) / 1000;

  if (!isPositiveFinite(screenWidth) || !isPositiveFinite(screenHeight) || !isPositiveFinite(ratio)) {
    return null;
  }
  return { screenWidth, screenHeight, devicePixelRatio: ratio };
}

/** The header value, or null when there is nothing worth sending. */
export function encodeClientDevice(device: ClientDevice | null): string | null {
  return device ? JSON.stringify(device) : null;
}

/**
 * The header value for the live browser. Returns null outside one (tests,
 * SSR), which is why both callers guard on it rather than sending an empty
 * header.
 */
export function clientDeviceHeader(): string | null {
  if (typeof window === 'undefined') return null;
  return encodeClientDevice(collectClientDevice(window));
}

function isPositiveFinite(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}
